// S1000D Data Module type catalogue and default constants
// Codes follow common S1000D Issue 5 information codes (indicative).

import ISO_3166_COUNTRIES from "../../../data/iso3166Countries";

export const DM_TYPES = [
  { code: "040", name: "Descriptive", description: "General description of the system, sub-system or component." },
  { code: "520", name: "Procedural", description: "Removal, installation, servicing and operational procedures." },
  { code: "420", name: "Fault Isolation", description: "Fault diagnostic and isolation procedures." },
  { code: "720", name: "Maintenance Planning", description: "Scheduled maintenance, time limits and inspection planning." },
  { code: "941", name: "Illustrated Parts Data (IPD)", description: "Parts catalogue with illustrations and part numbers." },
  { code: "057", name: "Wiring", description: "Wiring data including wiring diagrams and harness data." },
  { code: "PROC", name: "Process", description: "Process specification data modules." },
  { code: "022", name: "Crew / Operator", description: "Crew or operator information for normal & emergency operations." },
  { code: "SB", name: "Service Bulletin", description: "Service bulletin and modification instructions." },
  { code: "ACT", name: "Applicability Cross-Ref Table", description: "Cross-reference of applicability values." },
  { code: "PCT", name: "Product Cross-Ref Table", description: "Cross-reference of products/variants." },
  { code: "CCT", name: "Conditions Cross-Ref Table", description: "Cross-reference of conditions." },
  { code: "CONT", name: "Container", description: "Container data module for linking related modules." },
  { code: "LEARN", name: "Learning", description: "Learning and training data modules." },
  { code: "TR", name: "Technical Repository", description: "Technical repository data module." },
  { code: "CIR", name: "Common Information Repository", description: "Common reusable information repository." },
  { code: "FM", name: "Front Matter", description: "Publication front matter (title, TOC, list of effective)." },
  { code: "SCH", name: "Schedule", description: "Maintenance schedule data module." },
];

export const LEVELS = [
  { key: "simple", label: "Simple", subtitle: "System level", defaultHours: 6 },
  { key: "intermediate", label: "Intermediate", subtitle: "Sub-system level", defaultHours: 8 },
  { key: "complex", label: "Complex", subtitle: "Sub-subsystem level", defaultHours: 12 },
];

export const ILLUSTRATION_LEVELS = [
  { key: "simple", label: "Simple", subtitle: "Basic line / call-outs", defaultHours: 4 },
  { key: "intermediate", label: "Intermediate", subtitle: "Exploded / multi-view", defaultHours: 8 },
  { key: "complex", label: "Complex", subtitle: "Detailed / 3D / cutaway", defaultHours: 12 },
];

// Indicative S1000D project role allocations (sum = 100)
export const DEFAULT_ROLES = [
  { name: "Author", hourlyRate: 100, effortPercent: 30 },
  { name: "Illustrator", hourlyRate: 100, effortPercent: 18 },
  { name: "Subject Matter Expert (SME)", hourlyRate: 100, effortPercent: 12 },
  { name: "Editor / Quality Reviewer", hourlyRate: 100, effortPercent: 10 },
  { name: "Project Manager", hourlyRate: 100, effortPercent: 8 },
  { name: "S1000D Specialist / CSDB Manager", hourlyRate: 100, effortPercent: 8 },
  { name: "Validator / Verifier", hourlyRate: 100, effortPercent: 6 },
  { name: "Graphics Lead", hourlyRate: 100, effortPercent: 5 },
  { name: "Illustration Validator", hourlyRate: 100, effortPercent: 3 },
];

export const buildEmptyDmMatrix = () =>
  DM_TYPES.reduce((acc, t) => {
    acc[t.code] = {
      simple: { qty: 0, hours: 6 },
      intermediate: { qty: 0, hours: 8 },
      complex: { qty: 0, hours: 12 },
    };
    return acc;
  }, {});

export const buildEmptyIllustrations = () => ({
  simple: { qty: 0, hours: 4 },
  intermediate: { qty: 0, hours: 8 },
  complex: { qty: 0, hours: 12 },
});

export const CHART_COLORS = [
  "#002FA7", "#FF2400", "#111827", "#4B5563", "#7C3AED",
  "#0891B2", "#CA8A04", "#15803D", "#BE185D", "#1F2937",
];

// Comprehensive ISO 4217 currency list — covers all UN member states plus
// commonly-used regional currencies.  `fxSupported` marks the subset that
// frankfurter.dev / ECB publishes rates for; the Compare tab falls back to
// rate=1.0 for any currency outside that set.
export const CURRENCIES = [
  // ---- Major reserve currencies ----
  { code: "USD", symbol: "$",   label: "US Dollar",                fxSupported: true },
  { code: "EUR", symbol: "€",   label: "Euro",                     fxSupported: true },
  { code: "GBP", symbol: "£",   label: "British Pound",            fxSupported: true },
  { code: "JPY", symbol: "¥",   label: "Japanese Yen",             fxSupported: true },
  { code: "CHF", symbol: "CHF", label: "Swiss Franc",              fxSupported: true },
  { code: "CNY", symbol: "¥",   label: "Chinese Yuan",             fxSupported: true },
  // ---- Americas ----
  { code: "AUD", symbol: "A$",  label: "Australian Dollar",        fxSupported: true },
  { code: "CAD", symbol: "C$",  label: "Canadian Dollar",          fxSupported: true },
  { code: "NZD", symbol: "NZ$", label: "New Zealand Dollar",       fxSupported: true },
  { code: "BRL", symbol: "R$",  label: "Brazilian Real",           fxSupported: true },
  { code: "MXN", symbol: "Mex$",label: "Mexican Peso",             fxSupported: true },
  { code: "ARS", symbol: "AR$", label: "Argentine Peso" },
  { code: "CLP", symbol: "CL$", label: "Chilean Peso" },
  { code: "COP", symbol: "Col$",label: "Colombian Peso" },
  { code: "PEN", symbol: "S/",  label: "Peruvian Sol" },
  { code: "UYU", symbol: "$U",  label: "Uruguayan Peso" },
  { code: "VES", symbol: "Bs.S",label: "Venezuelan Bolívar" },
  { code: "BOB", symbol: "Bs.", label: "Bolivian Boliviano" },
  { code: "CRC", symbol: "₡",   label: "Costa Rican Colón" },
  { code: "DOP", symbol: "RD$", label: "Dominican Peso" },
  { code: "GTQ", symbol: "Q",   label: "Guatemalan Quetzal" },
  { code: "HNL", symbol: "L",   label: "Honduran Lempira" },
  { code: "JMD", symbol: "J$",  label: "Jamaican Dollar" },
  { code: "NIO", symbol: "C$",  label: "Nicaraguan Córdoba" },
  { code: "PAB", symbol: "B/.", label: "Panamanian Balboa" },
  { code: "PYG", symbol: "₲",   label: "Paraguayan Guaraní" },
  { code: "TTD", symbol: "TT$", label: "Trinidad and Tobago Dollar" },
  // ---- Europe ----
  { code: "SEK", symbol: "kr",  label: "Swedish Krona",            fxSupported: true },
  { code: "NOK", symbol: "kr",  label: "Norwegian Krone",          fxSupported: true },
  { code: "DKK", symbol: "kr",  label: "Danish Krone",             fxSupported: true },
  { code: "ISK", symbol: "kr",  label: "Icelandic Króna",          fxSupported: true },
  { code: "PLN", symbol: "zł",  label: "Polish Złoty",             fxSupported: true },
  { code: "CZK", symbol: "Kč",  label: "Czech Koruna",             fxSupported: true },
  { code: "HUF", symbol: "Ft",  label: "Hungarian Forint",         fxSupported: true },
  { code: "RON", symbol: "lei", label: "Romanian Leu",             fxSupported: true },
  { code: "BGN", symbol: "лв",  label: "Bulgarian Lev",            fxSupported: true },
  { code: "TRY", symbol: "₺",   label: "Turkish Lira",             fxSupported: true },
  { code: "RUB", symbol: "₽",   label: "Russian Ruble" },
  { code: "UAH", symbol: "₴",   label: "Ukrainian Hryvnia" },
  { code: "RSD", symbol: "дин", label: "Serbian Dinar" },
  { code: "HRK", symbol: "kn",  label: "Croatian Kuna" },
  { code: "ALL", symbol: "L",   label: "Albanian Lek" },
  { code: "MKD", symbol: "ден", label: "Macedonian Denar" },
  { code: "BAM", symbol: "KM",  label: "Bosnia-Herzegovina Mark" },
  { code: "MDL", symbol: "L",   label: "Moldovan Leu" },
  { code: "GEL", symbol: "₾",   label: "Georgian Lari" },
  { code: "AMD", symbol: "֏",   label: "Armenian Dram" },
  { code: "AZN", symbol: "₼",   label: "Azerbaijani Manat" },
  { code: "BYN", symbol: "Br",  label: "Belarusian Ruble" },
  // ---- Middle East & Africa ----
  { code: "ILS", symbol: "₪",   label: "Israeli Shekel",           fxSupported: true },
  { code: "ZAR", symbol: "R",   label: "South African Rand",       fxSupported: true },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "SAR", symbol: "﷼",   label: "Saudi Riyal" },
  { code: "QAR", symbol: "ر.ق", label: "Qatari Riyal" },
  { code: "KWD", symbol: "د.ك", label: "Kuwaiti Dinar" },
  { code: "BHD", symbol: "د.ب", label: "Bahraini Dinar" },
  { code: "OMR", symbol: "ر.ع.",label: "Omani Rial" },
  { code: "JOD", symbol: "د.أ", label: "Jordanian Dinar" },
  { code: "LBP", symbol: "ل.ل", label: "Lebanese Pound" },
  { code: "EGP", symbol: "ج.م", label: "Egyptian Pound" },
  { code: "IQD", symbol: "ع.د", label: "Iraqi Dinar" },
  { code: "IRR", symbol: "﷼",   label: "Iranian Rial" },
  { code: "YER", symbol: "﷼",   label: "Yemeni Rial" },
  { code: "SYP", symbol: "£S",  label: "Syrian Pound" },
  { code: "MAD", symbol: "د.م.",label: "Moroccan Dirham" },
  { code: "DZD", symbol: "د.ج", label: "Algerian Dinar" },
  { code: "TND", symbol: "د.ت", label: "Tunisian Dinar" },
  { code: "LYD", symbol: "ل.د", label: "Libyan Dinar" },
  { code: "NGN", symbol: "₦",   label: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", label: "Kenyan Shilling" },
  { code: "GHS", symbol: "₵",   label: "Ghanaian Cedi" },
  { code: "ETB", symbol: "Br",  label: "Ethiopian Birr" },
  { code: "UGX", symbol: "USh", label: "Ugandan Shilling" },
  { code: "TZS", symbol: "TSh", label: "Tanzanian Shilling" },
  { code: "ZMW", symbol: "ZK",  label: "Zambian Kwacha" },
  { code: "MUR", symbol: "₨",   label: "Mauritian Rupee" },
  { code: "BWP", symbol: "P",   label: "Botswana Pula" },
  { code: "NAD", symbol: "N$",  label: "Namibian Dollar" },
  { code: "AOA", symbol: "Kz",  label: "Angolan Kwanza" },
  { code: "MZN", symbol: "MT",  label: "Mozambican Metical" },
  { code: "XOF", symbol: "CFA", label: "West African CFA Franc" },
  { code: "XAF", symbol: "FCFA",label: "Central African CFA Franc" },
  // ---- Asia-Pacific ----
  { code: "INR", symbol: "₹",   label: "Indian Rupee",             fxSupported: true },
  { code: "HKD", symbol: "HK$", label: "Hong Kong Dollar",         fxSupported: true },
  { code: "SGD", symbol: "S$",  label: "Singapore Dollar",         fxSupported: true },
  { code: "KRW", symbol: "₩",   label: "South Korean Won",         fxSupported: true },
  { code: "MYR", symbol: "RM",  label: "Malaysian Ringgit",        fxSupported: true },
  { code: "IDR", symbol: "Rp",  label: "Indonesian Rupiah",        fxSupported: true },
  { code: "PHP", symbol: "₱",   label: "Philippine Peso",          fxSupported: true },
  { code: "THB", symbol: "฿",   label: "Thai Baht",                fxSupported: true },
  { code: "VND", symbol: "₫",   label: "Vietnamese Dong" },
  { code: "TWD", symbol: "NT$", label: "Taiwan Dollar" },
  { code: "PKR", symbol: "₨",   label: "Pakistani Rupee" },
  { code: "BDT", symbol: "৳",   label: "Bangladeshi Taka" },
  { code: "LKR", symbol: "Rs",  label: "Sri Lankan Rupee" },
  { code: "NPR", symbol: "₨",   label: "Nepalese Rupee" },
  { code: "MMK", symbol: "K",   label: "Myanmar Kyat" },
  { code: "KHR", symbol: "៛",   label: "Cambodian Riel" },
  { code: "LAK", symbol: "₭",   label: "Lao Kip" },
  { code: "MNT", symbol: "₮",   label: "Mongolian Tögrög" },
  { code: "KZT", symbol: "₸",   label: "Kazakhstani Tenge" },
  { code: "UZS", symbol: "soʻm",label: "Uzbekistani Som" },
  { code: "KGS", symbol: "с",   label: "Kyrgyzstani Som" },
  { code: "TJS", symbol: "SM",  label: "Tajikistani Somoni" },
  { code: "AFN", symbol: "؋",   label: "Afghan Afghani" },
  { code: "MOP", symbol: "MOP$",label: "Macanese Pataca" },
  { code: "FJD", symbol: "FJ$", label: "Fijian Dollar" },
  { code: "PGK", symbol: "K",   label: "Papua New Guinean Kina" },
  { code: "WST", symbol: "WS$", label: "Samoan Tala" },
  { code: "TOP", symbol: "T$",  label: "Tongan Paʻanga" },
];

CURRENCIES.sort((a, b) => a.code.localeCompare(b.code));

export const DEFAULT_CURRENCY = CURRENCIES.find((c) => c.code === "AUD");

export const findCurrency = (code) =>
  CURRENCIES.find((c) => c.code === code) || DEFAULT_CURRENCY;

// Frankfurter / ECB supported subset — used by Compare tab + share metadata
// to know which conversions are reliable.
export const FX_SUPPORTED_CODES = CURRENCIES
  .filter((c) => c.fxSupported)
  .map((c) => c.code);

// ISO day-of-week: 0=Sun, 1=Mon, ..., 6=Sat
const FRI_SAT_WEEKEND = ["AE", "SA", "QA", "KW", "BH", "OM", "EG", "JO", "IQ", "LY", "YE", "SY", "SD", "IL", "IR", "AF", "DZ", "BD", "MV"];
const PUBLIC_HOLIDAY_OVERRIDES = { US: 10, GB: 8, CA: 9, AU: 10, IN: 14, AE: 12, SA: 12, IL: 11 };

export const LOCATION_PRESETS = [
  ...[...ISO_3166_COUNTRIES]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({
      key: c.code,
      label: c.name,
      weekendDays: FRI_SAT_WEEKEND.includes(c.code) ? [5, 6] : [0, 6],
      publicHolidays: PUBLIC_HOLIDAY_OVERRIDES[c.code] ?? 10,
    })),
  { key: "CUSTOM", label: "Custom", weekendDays: [0, 6], publicHolidays: 0 },
];

const LEGACY_LOCATION_KEYS = { UK: "GB", EU: "DE", ME: "AE" };

export const findLocationPreset = (key) => {
  const k = LEGACY_LOCATION_KEYS[key] || key;
  return LOCATION_PRESETS.find((p) => p.key === k) || LOCATION_PRESETS.find((p) => p.key === "AU");
};

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const buildDefaultResourcePlan = () => ({
  startDate: "",
  endDate: "",
  locationPreset: "AU",
  weekendDays: [0, 6],
  publicHolidays: 10,
  standardHoursPerDay: 8,
  effectiveHoursPerDay: 6,
});

// --- Business Rules / BREX ---
export const S1000D_ISSUES = ["4.0", "4.1", "4.2", "5.0", "6.0"];

// Indicative business-rule decision-point counts per S1000D issue, organised
// by topical category. Values approximate the published BRDP groupings and are
// fully user-editable at runtime (qty per category cell).
export const BR_CATEGORIES = [
  { id: "project_planning",     name: "Project Planning & Management",  description: "Project structure, planning decisions and baseline applicability.",
    dpCounts: { "4.0": 18, "4.1": 20, "4.2": 22, "5.0": 24, "6.0": 26 } },
  { id: "info_sets_codes",      name: "Information Sets & Codes",       description: "Information codes, learning codes, info names selection.",
    dpCounts: { "4.0": 24, "4.1": 26, "4.2": 28, "5.0": 32, "6.0": 34 } },
  { id: "dm_structure",         name: "Data Module Structure",          description: "DMC composition, identification, status, applicability blocks.",
    dpCounts: { "4.0": 22, "4.1": 24, "4.2": 26, "5.0": 28, "6.0": 30 } },
  { id: "common_constructs",    name: "Common Constructs",              description: "Numbering, references, levelled paras, attribute conventions.",
    dpCounts: { "4.0": 30, "4.1": 32, "4.2": 34, "5.0": 36, "6.0": 38 } },
  { id: "descriptive_content",  name: "Descriptive Content",            description: "Descriptive DM structure, levels, allowed constructs.",
    dpCounts: { "4.0": 18, "4.1": 19, "4.2": 20, "5.0": 22, "6.0": 24 } },
  { id: "procedural_content",   name: "Procedural Content",             description: "Preliminary rqmts, main proc, close-out steps.",
    dpCounts: { "4.0": 26, "4.1": 28, "4.2": 30, "5.0": 32, "6.0": 34 } },
  { id: "fault_isolation",      name: "Fault Isolation",                description: "Fault DM structure, fault codes, isolation logic.",
    dpCounts: { "4.0": 16, "4.1": 18, "4.2": 20, "5.0": 22, "6.0": 24 } },
  { id: "maintenance_planning", name: "Maintenance Planning",           description: "Schedule, time limits and inspection planning rules.",
    dpCounts: { "4.0": 14, "4.1": 16, "4.2": 18, "5.0": 20, "6.0": 22 } },
  { id: "ipd",                  name: "Illustrated Parts Data (IPD)",   description: "IPD rules, parts, breakdowns, kits, attaching parts.",
    dpCounts: { "4.0": 28, "4.1": 30, "4.2": 32, "5.0": 36, "6.0": 38 } },
  { id: "wiring",               name: "Wiring Data",                    description: "Wiring DM, harness, connectors, equipment data.",
    dpCounts: { "4.0": 20, "4.1": 22, "4.2": 24, "5.0": 26, "6.0": 28 } },
  { id: "process",              name: "Process Data",                   description: "Process DM, parameter limits, references.",
    dpCounts: { "4.0": 12, "4.1": 13, "4.2": 14, "5.0": 16, "6.0": 18 } },
  { id: "crew_operator",        name: "Crew / Operator",                description: "Crew DM, normal & emergency operations.",
    dpCounts: { "4.0": 14, "4.1": 15, "4.2": 16, "5.0": 18, "6.0": 20 } },
  { id: "applicability",        name: "Applicability",                  description: "ACT / CCT / PCT rules and annotation strategy.",
    dpCounts: { "4.0": 22, "4.1": 24, "4.2": 26, "5.0": 30, "6.0": 32 } },
  { id: "illustration_rules",   name: "Illustration Rules",             description: "Hotspots, ICN naming, callouts, CGM/SVG choices.",
    dpCounts: { "4.0": 20, "4.1": 22, "4.2": 24, "5.0": 26, "6.0": 28 } },
  { id: "multimedia",           name: "Multimedia",                     description: "Multimedia objects, formats, captioning rules.",
    dpCounts: { "4.0": 8,  "4.1": 10, "4.2": 12, "5.0": 14, "6.0": 16 } },
  { id: "csdb_cir",             name: "CSDB & CIR",                     description: "Common Information Repository, exchange, CSDB rules.",
    dpCounts: { "4.0": 16, "4.1": 18, "4.2": 20, "5.0": 22, "6.0": 24 } },
  { id: "learning",             name: "Learning Content",               description: "Learning DM and didactic decisions (introduced in 4.1).",
    dpCounts: { "4.0": 0,  "4.1": 12, "4.2": 14, "5.0": 18, "6.0": 20 } },
  { id: "ietp_publication",     name: "IETP / Publication Module",      description: "PM, front matter, output channels, paper publication.",
    dpCounts: { "4.0": 18, "4.1": 20, "4.2": 22, "5.0": 24, "6.0": 26 } },
];

export const BREX_DEFAULT_ROLES = [
  { name: "S1000D Specialist",            hourlyRate: 120, effortPercent: 40 },
  { name: "BREX Developer",               hourlyRate: 115, effortPercent: 30 },
  { name: "Subject Matter Expert (SME)",  hourlyRate: 110, effortPercent: 15 },
  { name: "Project Manager",              hourlyRate: 130, effortPercent: 10 },
  { name: "Quality / Verifier",           hourlyRate: 105, effortPercent: 5  },
];

// Maps BR categories to the DM type codes they typically govern. "*" = applies to all DM types.
export const BR_CATEGORY_DM_MAP = {
  project_planning:     ["FM", "SCH", "CONT"],
  info_sets_codes:      ["*"],
  dm_structure:         ["*"],
  common_constructs:    ["*"],
  descriptive_content:  ["040"],
  procedural_content:   ["520", "022"],
  fault_isolation:      ["420"],
  maintenance_planning: ["720", "SCH"],
  ipd:                  ["941"],
  wiring:               ["057"],
  process:              ["PROC"],
  crew_operator:        ["022"],
  applicability:        ["ACT", "PCT", "CCT", "*"],
  illustration_rules:   ["*"],
  multimedia:           ["*"],
  csdb_cir:             ["CIR", "TR"],
  learning:             ["LEARN"],
  ietp_publication:     ["FM"],
};

// Indicative XPath roots a BREX rule typically targets per category (illustrative; users will refine).
export const BR_CATEGORY_BREX_PATHS = {
  project_planning:     "//dmodule/identAndStatusSection",
  info_sets_codes:      "//dmCode/@infoCode",
  dm_structure:         "//dmodule/content",
  common_constructs:    "//*[@id]",
  descriptive_content:  "//description",
  procedural_content:   "//proceduralStep",
  fault_isolation:      "//fault | //isolationStep",
  maintenance_planning: "//schedule | //timeLimits",
  ipd:                  "//catalogSeqNumber",
  wiring:               "//wiringData",
  process:              "//processStep",
  crew_operator:        "//crew | //operator",
  applicability:        "//applic | //applicCrossRefTable",
  illustration_rules:   "//figure | //graphic | //hotspot",
  multimedia:           "//multimedia",
  csdb_cir:             "//commonInfoDescr",
  learning:             "//learningContent",
  ietp_publication:     "//pmEntry",
};

export const buildDefaultBrexPlan = () => ({
  s1000dIssue: "6.0",
  defineMinutesPerDP: 10,
  brexMinutesPerDP: 5,
  includeInProjectTotal: false,
  categoryInclusions: BR_CATEGORIES.reduce((acc, c) => { acc[c.id] = true; return acc; }, {}),
  categoryDpOverrides: {},
  roles: BREX_DEFAULT_ROLES.map((r) => ({ ...r })),
});

// --- Data Management List (DML) — S1000D SNS presets ---
// Curated subset of the maintained / example SNS structures published in S1000D
// chap 8.7 (Standard Numbering System). Each entry is illustrative; users edit at runtime.
export const SNS_PRESETS = [
  {
    id: "generic",
    name: "Generic SNS (S1000D Chap 8.7)",
    description: "Minimal generic baseline.",
    systems: [
      { code: "00", name: "Generic", subsystems: [
        { code: "00", name: "General", subsubsystems: [{ code: "00", name: "Common" }] },
      ]},
    ],
  },
  {
    id: "aircraft",
    name: "Aircraft — Maintained SNS (ATA-100 aligned)",
    description: "Representative aircraft chapters.",
    systems: [
      { code: "00", name: "General", subsystems: [
        { code: "00", name: "Introduction", subsubsystems: [{ code: "00", name: "Common" }] }] },
      { code: "21", name: "Air conditioning", subsystems: [
        { code: "10", name: "Compression", subsubsystems: [{ code: "00", name: "General" }, { code: "10", name: "Compressor" }] },
        { code: "20", name: "Distribution", subsubsystems: [{ code: "00", name: "Ducting" }] }] },
      { code: "24", name: "Electrical power", subsystems: [
        { code: "20", name: "AC generation", subsubsystems: [{ code: "00", name: "Generator" }] },
        { code: "30", name: "DC generation", subsubsystems: [{ code: "00", name: "Battery" }] }] },
      { code: "27", name: "Flight controls", subsystems: [
        { code: "10", name: "Aileron", subsubsystems: [{ code: "00", name: "Surface" }] },
        { code: "20", name: "Rudder", subsubsystems: [{ code: "00", name: "Surface" }] },
        { code: "30", name: "Elevator", subsubsystems: [{ code: "00", name: "Surface" }] }] },
      { code: "32", name: "Landing gear", subsystems: [
        { code: "10", name: "Main gear", subsubsystems: [{ code: "00", name: "Strut" }, { code: "10", name: "Wheel" }] },
        { code: "20", name: "Nose gear", subsubsystems: [{ code: "00", name: "Strut" }] }] },
      { code: "71", name: "Power plant", subsystems: [
        { code: "10", name: "Cowling", subsubsystems: [{ code: "00", name: "Panel" }] },
        { code: "20", name: "Mounts", subsubsystems: [{ code: "00", name: "Support" }] }] },
      { code: "72", name: "Engine", subsystems: [
        { code: "10", name: "Reduction gear", subsubsystems: [{ code: "00", name: "Gearbox" }] },
        { code: "30", name: "Compressor", subsubsystems: [{ code: "00", name: "Stage" }] },
        { code: "50", name: "Turbine", subsubsystems: [{ code: "00", name: "Stage" }] }] },
    ],
  },
  {
    id: "helicopter",
    name: "Helicopter — Maintained SNS",
    description: "Rotorcraft-specific systems.",
    systems: [
      { code: "00", name: "General", subsystems: [{ code: "00", name: "Intro", subsubsystems: [{ code: "00", name: "Common" }] }] },
      { code: "62", name: "Main rotor", subsystems: [
        { code: "10", name: "Blades", subsubsystems: [{ code: "00", name: "Assy" }, { code: "10", name: "Skin" }] },
        { code: "20", name: "Hub", subsubsystems: [{ code: "00", name: "Hub assy" }] }] },
      { code: "63", name: "Main rotor drive", subsystems: [
        { code: "10", name: "Transmission", subsubsystems: [{ code: "00", name: "Gearbox" }] }] },
      { code: "64", name: "Tail rotor", subsystems: [
        { code: "10", name: "Blades", subsubsystems: [{ code: "00", name: "Assy" }] }] },
      { code: "67", name: "Rotor flight controls", subsystems: [
        { code: "10", name: "Collective", subsubsystems: [{ code: "00", name: "Linkage" }] },
        { code: "20", name: "Cyclic", subsubsystems: [{ code: "00", name: "Linkage" }] }] },
    ],
  },
  {
    id: "land",
    name: "Land Vehicle — Example SNS",
    description: "Wheeled / tracked vehicle baseline.",
    systems: [
      { code: "00", name: "General", subsystems: [{ code: "00", name: "Intro", subsubsystems: [{ code: "00", name: "Common" }] }] },
      { code: "10", name: "Powerpack", subsystems: [
        { code: "10", name: "Engine", subsubsystems: [{ code: "00", name: "Block" }, { code: "10", name: "Cooling" }] },
        { code: "20", name: "Transmission", subsubsystems: [{ code: "00", name: "Gearbox" }] }] },
      { code: "20", name: "Driveline", subsystems: [
        { code: "10", name: "Final drive", subsubsystems: [{ code: "00", name: "Sprocket" }] }] },
      { code: "30", name: "Hull / chassis", subsystems: [
        { code: "10", name: "Frame", subsubsystems: [{ code: "00", name: "Structure" }] }] },
      { code: "40", name: "Suspension", subsystems: [
        { code: "10", name: "Wheel station", subsubsystems: [{ code: "00", name: "Torsion bar" }] }] },
      { code: "70", name: "Armament", subsystems: [
        { code: "10", name: "Main gun", subsubsystems: [{ code: "00", name: "Barrel" }] }] },
    ],
  },
  {
    id: "surface_ship",
    name: "Surface Ship — Example SNS",
    description: "Naval vessel example structure.",
    systems: [
      { code: "00", name: "General", subsystems: [{ code: "00", name: "Intro", subsubsystems: [{ code: "00", name: "Common" }] }] },
      { code: "10", name: "Hull structure", subsystems: [
        { code: "10", name: "Plating", subsubsystems: [{ code: "00", name: "Section" }] }] },
      { code: "20", name: "Propulsion", subsystems: [
        { code: "10", name: "Engine room", subsubsystems: [{ code: "00", name: "Main engine" }] },
        { code: "20", name: "Shafting", subsubsystems: [{ code: "00", name: "Bearing" }] }] },
      { code: "30", name: "Electrical", subsystems: [
        { code: "10", name: "Generation", subsubsystems: [{ code: "00", name: "Generator" }] }] },
      { code: "50", name: "Auxiliary", subsystems: [
        { code: "10", name: "HVAC", subsubsystems: [{ code: "00", name: "Chiller" }] }] },
    ],
  },
  {
    id: "weapons",
    name: "Munitions / Weapons — Example SNS",
    description: "Weapon system structure.",
    systems: [
      { code: "00", name: "General", subsystems: [{ code: "00", name: "Intro", subsubsystems: [{ code: "00", name: "Common" }] }] },
      { code: "10", name: "Warhead", subsystems: [
        { code: "10", name: "Casing", subsubsystems: [{ code: "00", name: "Body" }] }] },
      { code: "20", name: "Guidance", subsystems: [
        { code: "10", name: "Seeker", subsubsystems: [{ code: "00", name: "Sensor" }] }] },
      { code: "30", name: "Propulsion", subsystems: [
        { code: "10", name: "Motor", subsubsystems: [{ code: "00", name: "Grain" }] }] },
    ],
  },
];

export const findSnsPreset = (id) => SNS_PRESETS.find((s) => s.id === id) || SNS_PRESETS[0];

export const countSnsLevels = (preset) => {
  if (!preset) return { systems: 0, subsystems: 0, subsubsystems: 0 };
  const systems = preset.systems.length;
  let subsystems = 0, subsubsystems = 0;
  for (const s of preset.systems) {
    subsystems += s.subsystems.length;
    for (const ss of s.subsystems) subsubsystems += ss.subsubsystems.length;
  }
  return { systems, subsystems, subsubsystems };
};

export const buildDefaultDmlPlan = () => ({
  snsPresetId: "generic",
  systemCount: 0,
  subsystemCount: 0,
  subsubsystemCount: 0,
  dmcCount: 0,
  icnCount: 0,
  pmcCount: 1,
  minutesPerDmc: 3,
  minutesPerIcn: 3,
  minutesPerPmc: 3,
  modelIdentCode: "PROJECT",
  systemDiffCode: "A",
});

// ---- Risk & Assumptions Register ----
export const RISK_CATEGORIES = [
  { id: "technical", label: "Technical" },
  { id: "schedule", label: "Schedule" },
  { id: "commercial", label: "Commercial" },
  { id: "sourceData", label: "Source data" },
  { id: "regulatory", label: "Regulatory / Standards" },
  { id: "resource", label: "Resource / Staffing" },
  { id: "supplier", label: "Supplier / 3rd party" },
];

export const RISK_LIKELIHOOD = [
  { v: 1, label: "Rare" },
  { v: 2, label: "Unlikely" },
  { v: 3, label: "Possible" },
  { v: 4, label: "Likely" },
  { v: 5, label: "Almost certain" },
];

export const RISK_IMPACT = [
  { v: 1, label: "Negligible" },
  { v: 2, label: "Minor" },
  { v: 3, label: "Moderate" },
  { v: 4, label: "Major" },
  { v: 5, label: "Severe" },
];

export const DEPENDENCY_TYPES = ["Internal", "Customer", "Supplier", "Regulator"];

export const buildDefaultRiskPlan = () => ({
  risks: [],
  assumptions: [],
  dependencies: [],
  autoContingency: true,
  maxAutoBumpPercent: 20,
});

// ---- Effort-by-Phase / Schedule ----
export const SCHEDULE_PHASES = [
  { key: "plan", label: "Plan", color: "#6B7280" },
  { key: "author", label: "Author", color: "#002FA7" },
  { key: "illustrate", label: "Illustrate", color: "#FF6B00" },
  { key: "review", label: "Review", color: "#8B5CF6" },
  { key: "validate", label: "Validate", color: "#10B981" },
  { key: "publish", label: "Publish", color: "#111827" },
];

export const buildDefaultSchedulePlan = () => ({
  phases: {
    plan: { percent: 8, startOffsetWeeks: 0, durationWeeks: 2 },
    author: { percent: 40, startOffsetWeeks: 2, durationWeeks: 12 },
    illustrate: { percent: 20, startOffsetWeeks: 3, durationWeeks: 10 },
    review: { percent: 15, startOffsetWeeks: 8, durationWeeks: 6 },
    validate: { percent: 10, startOffsetWeeks: 12, durationWeeks: 4 },
    publish: { percent: 7, startOffsetWeeks: 14, durationWeeks: 2 },
  },
  basisHours: "combined", // "combined" (project + BREX if merged) or "project"
});

// ---- Bid Price / Win Probability ----
export const buildDefaultBidPlan = () => ({
  targetMarginPercent: 20,
  winProbabilityPercent: 50,
  competitorPrice: 0,
  strategicAdjustmentPercent: 0,
  pricingBasis: "combined", // combined or project
});

// ---- System Breakdown Structure (SBS) ----
// Tree shape:
//   System  → Sub-System  → Sub-Subsystem
// Each node carries: { id, name, generalQty, repairableQty } and (for System &
// Sub-System) a child array. Quantities feed the DM Matrix when synced:
//   System  → Simple complexity (system-level item)
//   Sub-System → Intermediate (sub-system-level item)
//   Sub-Subsystem → Complex (sub-subsystem-level item)
// "General" items map to the generalDmType (default 040 Descriptive),
// "Repairable" items map to repairableDmType (default 520 Procedural).
const _newId = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const buildEmptySubsubsystem = () => ({
  id: _newId("ss"), name: "", generalQty: 0, repairableQty: 0,
});

export const buildEmptySubsystem = () => ({
  id: _newId("sub"), name: "", generalQty: 0, repairableQty: 0, subsubsystems: [],
});

export const buildEmptySystem = () => ({
  id: _newId("sys"), name: "", generalQty: 0, repairableQty: 0, subsystems: [],
});

export const buildDefaultSbsPlan = () => ({
  systems: [],
  generalDmType: "040",       // Descriptive
  repairableDmType: "520",    // Procedural
  hoursPerItem: { simple: 6, intermediate: 8, complex: 12 },
});

// Aggregate qty by complexity level across the tree.
export function computeSbsCounts(sbs) {
  const acc = {
    simple: { general: 0, repairable: 0 },
    intermediate: { general: 0, repairable: 0 },
    complex: { general: 0, repairable: 0 },
  };
  if (!sbs?.systems) return { ...acc, totalItems: 0 };
  for (const s of sbs.systems) {
    acc.simple.general += Number(s.generalQty) || 0;
    acc.simple.repairable += Number(s.repairableQty) || 0;
    for (const sub of (s.subsystems || [])) {
      acc.intermediate.general += Number(sub.generalQty) || 0;
      acc.intermediate.repairable += Number(sub.repairableQty) || 0;
      for (const ss of (sub.subsubsystems || [])) {
        acc.complex.general += Number(ss.generalQty) || 0;
        acc.complex.repairable += Number(ss.repairableQty) || 0;
      }
    }
  }
  const totalItems =
    acc.simple.general + acc.simple.repairable +
    acc.intermediate.general + acc.intermediate.repairable +
    acc.complex.general + acc.complex.repairable;
  return { ...acc, totalItems };
}

// ---- Reviews & Updates Plan ----
export const buildDefaultReviewPlan = () => ({
  percentages: [25, 50, 75],
});

